import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { ImpersonationDto } from '../../../shared/types/access';
import { authAtom } from './auth.service';
import { api } from './api';

/**
 * A Persera admin acting as another user. They can do what the user can, as the user, and every change is
 * logged. Only erasing the account and billing are refused (403 IMPERSONATION_NOT_ALLOWED), and the UI
 * hides those (see useIsImpersonating).
 */

/** The current impersonation (from /api/me), or null. */
export function useImpersonation(): ImpersonationDto | null {
    const user = useAtomValue(authAtom);
    return user ? user.impersonation ?? null : null;
}

/** True while acting as someone else: hide what only the user may do (erasing their account, billing). */
export function useIsImpersonating(): boolean {
    return !!useImpersonation();
}

/** "1h 52m", "4m", "under a minute". */
export function formatRemaining(ms: number): string {
    if (ms < 60_000) return 'under a minute';
    const minutes = Math.floor(ms / 60_000);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

export function useStartImpersonation() {
    return useMutation({
        mutationFn: async (guid: string) => (await api.post<ImpersonationDto>('/api/admin/impersonation', { guid })).data,
    });
}

export function useStopImpersonation() {
    return useMutation({
        mutationFn: async () => (await api.post<{ stopped: boolean }>('/api/admin/impersonation/stop')).data,
    });
}
